import React, { useEffect, useMemo, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CloseIcon from '@mui/icons-material/Close';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { FONT_STACK_SUISSE_WORKS } from '~/components/brand';

import { useGetReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useDismissLensAccessRequest, useGetLensAccessRequests } from '~/hooks/apis/reporting/useLensAccessRequests';
import { useShareLensWithCompany, useUpdateLensSharing } from '~/hooks/apis/reporting/useLensSharing';

import type { Lens, LensRole, LensShareRecipient, LensSpace } from '~/types/leLens';

import LensRecipientInput from './LensRecipientInput';
import LensShareExport from './LensShareExport';
import { companyShareState } from './companyShareState';
import { ownerDisplayName } from './lensLibrary';
import type { LensState } from './document/types';
import { lensShareUrl } from './lensShareContent';
import type { LensUrlState } from './lensUrlState';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Direct shares grant one of two things. Manager is held by the owner and by a space's managers. */
const RECIPIENT_ROLES: Array<{ value: LensRole; label: string }> = [
  { value: 'viewer', label: 'Can view' },
  { value: 'editor', label: 'Can edit' },
];

interface ShareLensDialogProps {
  open: boolean;
  onClose: () => void;
  lens: Lens | null;
  /** The spaces the signed-in employee belongs to, which are the only homes they can move a Lens into. */
  spaces: Array<LensSpace>;
  viewerEmail?: string | null;
  currentView?: LensUrlState;
  documentState?: LensState;
  spacesLoading?: boolean;
  spacesError?: boolean;
}

/**
 * One dialog for the whole question of who can see a Lens, disclosed in the order
 * a person decides it: what is true now, then the lightweight one-off share, then
 * a durable team home.
 */
export default function ShareLensDialog({
  open,
  onClose,
  lens,
  spaces,
  viewerEmail,
  currentView,
  documentState,
  spacesLoading,
  spacesError,
}: ShareLensDialogProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const updateSharing = useUpdateLensSharing();
  const shareCompany = useShareLensWithCompany();
  const companySubmitting = useRef(false);
  const [confirmCompanyShare, setConfirmCompanyShare] = useState(false);
  const [companyShareError, setCompanyShareError] = useState<string | null>(null);
  const [companyShared, setCompanyShared] = useState(false);
  const [includeView, setIncludeView] = useState(true);
  const [moveError, setMoveError] = useState<string | null>(null);
  const writableSpaces = spaces.filter((space) => space.role === 'editor' || space.role === 'manager');
  /**
   * The recipient list lives on the detail response, not the list one: naming the
   * colleagues a Lens was shared with is only shown to someone who may change it,
   * so it is not carried on every row of a library page.
   */
  const detail = useGetReportingLeLens(open && lens ? lens.id : undefined);
  const recipients = useMemo(() => detail.data?.sharing?.recipients ?? [], [detail.data]);
  const recipientsLoaded = !detail.isLoading && !detail.isError;

  const selectedLensRef = useRef(lens);
  selectedLensRef.current = lens;
  const hydratedLensId = useRef<string | null>(null);
  const [activeLens, setActiveLens] = useState<Lens | null>(null);
  const [draft, setDraft] = useState<Array<LensShareRecipient>>([]);
  const [confirmedRecipients, setConfirmedRecipients] = useState<Array<LensShareRecipient>>([]);
  const [invitee, setInvitee] = useState('');
  const [inviteeRole, setInviteeRole] = useState<LensRole>('viewer');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [sharingFeedback, setSharingFeedback] = useState<{ message: string; severity: 'error' | 'success' } | null>(null);
  const [moveTo, setMoveTo] = useState<string>('');
  const [newManager, setNewManager] = useState('');
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const currentLens = activeLens?.id === lens?.id ? activeLens : lens;
  const home = currentLens?.home;
  const currentSpaceId = home?.kind === 'space' ? home.spaceId : '';
  const canShare = !detail.isError && Boolean(detail.data?.access?.canShare ?? currentLens?.access?.canShare);
  const effectiveAccess = detail.data?.access ?? currentLens?.access;
  const canShareEdit = Boolean(effectiveAccess?.canShareEdit ?? effectiveAccess?.canEdit);
  const canManageSharing = Boolean(effectiveAccess?.canManageSharing ?? effectiveAccess?.role === 'manager');
  const allowedRecipientRoles = RECIPIENT_ROLES.filter((role) => canShareEdit || role.value === 'viewer');
  const busy = updateSharing.isPending || shareCompany.isPending || companySubmitting.current || detail.isLoading;
  const companyState = companyShareState(detail.data, companyShared);
  const alreadySharedWithCompany = companyState === 'shared';
  const canShareCompany = companyState === 'available';
  const accessRequests = useGetLensAccessRequests(currentLens?.id ?? '', open && canShare && Boolean(currentLens));
  const dismissAccessRequest = useDismissLensAccessRequest(currentLens?.id ?? '');

  useEffect(() => {
    const selectedLens = selectedLensRef.current;
    if (open && selectedLens) {
      hydratedLensId.current = null;
      setActiveLens(selectedLens);
      setDraft([]);
      setConfirmedRecipients([]);
      setInvitee('');
      setInviteeRole('viewer');
      setInviteError(null);
      setSharingFeedback(null);
      setMoveTo(selectedLens.home?.kind === 'space' ? selectedLens.home.spaceId : '');
      setNewManager(viewerEmail ?? '');
      setMoveError(null);
      setCopied(false);
      setCopyError(false);
      setIncludeView(true);
      setConfirmCompanyShare(false);
      setCompanyShareError(null);
      setCompanyShared(false);
    }
  }, [lens?.id, open, viewerEmail]);

  // Hydrate the editable draft once per opening. A refetch after a mutation must
  // not overwrite a role the reader is in the middle of changing.
  useEffect(() => {
    if (!open || !lens || !detail.data || hydratedLensId.current === lens.id) {
      return;
    }
    const detailedLens: Lens = {
      ...detail.data.lens,
      access: { ...lens.access, ...detail.data.access },
      audience: detail.data.sharing?.audience ?? detail.data.lens.audience,
      home: detail.data.sharing?.home ?? detail.data.lens.home,
      recipientCount: detail.data.sharing?.recipientCount ?? detail.data.lens.recipientCount,
    };
    hydratedLensId.current = lens.id;
    setActiveLens(detailedLens);
    setDraft(recipients);
    setConfirmedRecipients(recipients);
    setMoveTo(detailedLens.home?.kind === 'space' ? detailedLens.home.spaceId : '');
  }, [detail.data, lens, open, recipients]);

  const sharedView = includeView ? currentView : undefined;
  const viewState = documentState && Object.keys(documentState).length ? documentState : undefined;
  const shareUrl = currentLens ? lensShareUrl(window.location.origin, currentLens.id, sharedView, includeView ? viewState : undefined) : '';

  /**
   * The copy-link feedback has to say whether the link is enough on its own. A
   * link to a private Lens is a link to a refusal, and a person who was not told
   * that finds out from the colleague they sent it to.
   */
  const linkReach =
    currentLens?.audience === 'reporting-admins'
      ? 'Link copied. The creator and reporting admins can open this Lens; add anyone else below.'
      : currentLens?.audience === 'company'
        ? 'Link copied. Every employee can open this Lens.'
        : confirmedRecipients.length > 0 ||
            currentSpaceId ||
            currentLens?.audience === 'people' ||
            currentLens?.audience === 'team'
          ? 'Link copied. Reporting admins and the people and teams listed here can open it.'
          : 'Link copied. The creator and reporting admins can open this Lens; add anyone else below.';

  if (!currentLens) {
    return null;
  }

  const mergeActiveLens = (next: Lens) => {
    setActiveLens((current) => ({ ...next, access: current?.access ?? lens?.access }));
  };

  const confirmShareWithCompany = async () => {
    if (companySubmitting.current || !canShareCompany || !currentLens) {
      return;
    }
    companySubmitting.current = true;
    setCompanyShareError(null);
    try {
      const result = await shareCompany.mutateAsync(currentLens.id);
      if (result.lens.status !== 'published') {
        throw new Error('The service did not confirm company sharing. Close and reopen this panel to check.');
      }
      setCompanyShared(true);
      setConfirmCompanyShare(false);
      mergeActiveLens(result.lens);
    } catch (error) {
      setCompanyShareError(error instanceof Error ? error.message : 'We couldn’t share this report. Try again.');
    } finally {
      companySubmitting.current = false;
    }
  };

  const draftWithInvitee = (): Array<LensShareRecipient> | null => {
    const email = invitee.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      setInviteError('Enter a work email address');
      return null;
    }
    if (!currentSpaceId && email === currentLens.ownerEmail.trim().toLowerCase()) {
      setInviteError('This person already manages the Lens');
      return null;
    }
    if (draft.some((person) => person.email === email)) {
      setInviteError('This person is already on the list');
      return null;
    }
    return [...draft, { email, role: inviteeRole }];
  };

  const addRecipient = async () => {
    const next = draftWithInvitee();
    if (!next) return;
    const person = next[next.length - 1];
    if (await grantAccess(person.email, person.role)) {
      setInvitee('');
      setInviteError(null);
    }
  };

  const grantAccess = async (email: string, role: LensRole = 'viewer') => {
    setSharingFeedback(null);
    try {
      const result = await updateSharing.mutateAsync({
        lensId: currentLens.id,
        grants: [{ email, role }],
      });
      const unsaved = draft.filter(
        (person) =>
          person.email !== email &&
          !confirmedRecipients.some((saved) => saved.email === person.email && saved.role === person.role),
      );
      const removed = confirmedRecipients.filter(
        (saved) => saved.email !== email && !draft.some((person) => person.email === saved.email),
      );
      setConfirmedRecipients(result.recipients);
      setDraft([
        ...result.recipients.filter(
          (saved) =>
            !unsaved.some((person) => person.email === saved.email) &&
            !removed.some((person) => person.email === saved.email),
        ),
        ...unsaved,
      ]);
      mergeActiveLens(result.lens);
      setSharingFeedback({ message: `${email} can now open this Lens`, severity: 'success' });
      return true;
    } catch (error) {
      setSharingFeedback({
        message: error instanceof Error ? error.message : 'We couldn’t add this person. Try again.',
        severity: 'error',
      });
      return false;
    } finally {
      await accessRequests.refetch();
    }
  };

  const removeRecipient = async (email: string) => {
    setSharingFeedback(null);
    try {
      const result = await updateSharing.mutateAsync({
        lensId: currentLens.id,
        recipients: confirmedRecipients
          .filter((person) => person.email !== email)
          .map(({ email, role }) => ({ email, role })),
      });
      setDraft((current) => current.filter((person) => person.email !== email));
      setConfirmedRecipients(result.recipients);
      mergeActiveLens(result.lens);
      setSharingFeedback({ message: `${email} can no longer open this Lens`, severity: 'success' });
    } catch (error) {
      setSharingFeedback({
        message: error instanceof Error ? error.message : 'We couldn’t remove this person. Try again.',
        severity: 'error',
      });
      return;
    }
  };

  const saveChanges = async () => {
    const result = await updateSharing.mutateAsync({
      lensId: currentLens.id,
      ...(recipientsChanged
        ? canManageSharing
          ? { recipients: draft.map(({ email, role }) => ({ email, role })) }
          : {
              grants: draft
                .filter(
                  (person) =>
                    !confirmedRecipients.some((saved) => saved.email === person.email && saved.role === person.role),
                )
                .map(({ email, role }) => ({ email, role })),
            }
        : {}),
      ...(homeChanged
        ? { spaceId: moveTo || null, ownerEmail: leavingSpace ? newManager.trim().toLowerCase() : undefined }
        : {}),
    });
    setDraft(result.recipients);
    setConfirmedRecipients(result.recipients);
    mergeActiveLens(result.lens);
    setMoveTo(result.lens.home?.kind === 'space' ? result.lens.home.spaceId : '');
  };

  const leavingSpace = Boolean(currentSpaceId) && !moveTo;
  const homeChanged = (moveTo || '') !== (currentSpaceId || '');
  const recipientsChanged =
    draft.length !== confirmedRecipients.length ||
    draft.some(
      (person) => !confirmedRecipients.some((saved) => saved.email === person.email && saved.role === person.role),
    );
  const label = (text: string) => (
    <Typography
      sx={{
        color: tokens.accent,
        fontSize: 11,
        fontWeight: 750,
        letterSpacing: '0.09em',
        mb: 1,
        mt: 3,
        textTransform: 'uppercase',
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Dialog
      PaperProps={{
        sx: {
          bgcolor: tokens.canvas,
          borderRadius: 0,
          height: '100%',
          m: 0,
          maxHeight: '100%',
          width: { xs: '100%', sm: 440 },
        },
      }}
      onClose={busy ? undefined : onClose}
      open={open}
      maxWidth={false}
      aria-labelledby="lens-share-title"
      sx={{ '& .MuiDialog-container': { justifyContent: 'flex-end' } }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: alpha(tokens.accent, 0.16), px: 3, py: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              color: tokens.accent,
              fontSize: 11,
              fontWeight: 750,
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
            }}
            title={currentLens.title}
          >
            {currentLens.title}
          </Typography>
          <Typography
            component="h2"
            id="lens-share-title"
            sx={{ color: tokens.accent, fontFamily: FONT_STACK_SUISSE_WORKS, fontSize: 25, fontWeight: 400 }}
          >
            Share Lens
          </Typography>
        </Box>
        <IconButton aria-label="Close" disabled={busy} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      {sharingFeedback && (
        <Alert
          role={sharingFeedback.severity === 'success' ? 'status' : 'alert'}
          severity={sharingFeedback.severity}
          sx={{ borderRadius: 0, flexShrink: 0, px: 3, py: 0.5 }}
        >
          {sharingFeedback.message}
        </Alert>
      )}

      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 3, pb: 3 }}>
        {!canShare && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You do not have permission to share this Lens.
          </Alert>
        )}

        {detail.isError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            We couldn’t load who this Lens is already shared with, so saving now could remove someone. Close this and
            try again.
          </Alert>
        )}

        {canShare && accessRequests.isError && (
          <>
            {label('Requests')}
            <Button
              disabled={accessRequests.isFetching}
              onClick={() => void accessRequests.refetch()}
              size="small"
              variant="outlined"
            >
              Try again
            </Button>
          </>
        )}
        {canShare && Boolean(accessRequests.data?.length) && (
          <>
            {label('Requests')}
            {accessRequests.data?.map((accessRequest) => (
              <Stack alignItems="center" direction="row" key={accessRequest.email} spacing={1} sx={{ py: 0.5 }}>
                <Typography noWrap sx={{ flex: 1, fontSize: 12.5, minWidth: 0 }} title={accessRequest.email}>
                  {ownerDisplayName(accessRequest.email)}
                </Typography>
                <Button
                  aria-label={`Add ${accessRequest.email}`}
                  disabled={busy || !recipientsLoaded}
                  onClick={() => void grantAccess(accessRequest.email)}
                  size="small"
                  variant="contained"
                >
                  Add
                </Button>
                <Button
                  aria-label={`Dismiss ${accessRequest.email}`}
                  disabled={dismissAccessRequest.isPending}
                  onClick={() => dismissAccessRequest.mutate(accessRequest.email)}
                  size="small"
                >
                  Dismiss
                </Button>
              </Stack>
            ))}
          </>
        )}
        {label('Share with people')}
        <Stack direction="row" spacing={1}>
          <LensRecipientInput
            disabled={!open || !canShare || busy}
            value={invitee}
            error={inviteError}
            excludedEmails={[
              ...draft.map((person) => person.email),
              ...(!currentSpaceId ? [currentLens.ownerEmail] : []),
            ]}
            onChange={(value) => {
              setInvitee(value);
              setInviteError(null);
              setSharingFeedback(null);
            }}
          />
          <TextField
            disabled={!canShare || busy}
            inputProps={{ 'aria-label': 'Access for the person being added' }}
            onChange={(event) => {
              setInviteeRole(event.target.value as LensRole);
              setSharingFeedback(null);
            }}
            select
            size="small"
            sx={{ minWidth: 118 }}
            value={inviteeRole}
          >
            {allowedRecipientRoles.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Button
          disabled={!canShare || busy || !invitee.trim()}
          onClick={() => void addRecipient()}
          size="small"
          sx={{ mt: 1 }}
          variant="outlined"
        >
          Add
        </Button>
        <Stack sx={{ mt: 1.5 }}>
          {draft.map((person) => (
            <Stack
              alignItems="center"
              direction="row"
              key={person.email}
              spacing={1.25}
              sx={{ borderBottom: '1px solid', borderColor: alpha(tokens.accent, 0.12), py: 1.25 }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(tokens.accent, 0.12),
                  color: tokens.accent,
                  fontSize: 11,
                  height: 28,
                  width: 28,
                }}
              >
                {person.email.charAt(0).toUpperCase()}
              </Avatar>
              <Typography noWrap sx={{ flex: 1, fontSize: 12.5, minWidth: 0 }} title={person.email}>
                {person.email}
              </Typography>
              <TextField
                disabled={!canShare || busy || (!canShareEdit && person.role === 'editor')}
                inputProps={{ 'aria-label': `Access for ${person.email}` }}
                onChange={(event) => {
                  setSharingFeedback(null);
                  setDraft((current) =>
                    current.map((entry) =>
                      entry.email === person.email ? { ...entry, role: event.target.value as LensRole } : entry,
                    ),
                  );
                }}
                select
                size="small"
                sx={{ minWidth: 112, '& .MuiSelect-select': { fontSize: 12 } }}
                value={person.role}
              >
                {RECIPIENT_ROLES.map((role) => (
                  <MenuItem
                    key={role.value}
                    disabled={
                      (!canShareEdit && role.value === 'editor') ||
                      (!canManageSharing && person.role === 'editor' && role.value === 'viewer')
                    }
                    sx={{ fontSize: 12.5 }}
                    value={role.value}
                  >
                    {role.label}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title={`Remove ${person.email}`}>
                <span>
                  <IconButton
                    aria-label={`Remove ${person.email}`}
                    disabled={!canManageSharing || busy || !recipientsLoaded}
                    onClick={() => {
                      void removeRecipient(person.email);
                    }}
                    size="small"
                  >
                    <PersonRemoveOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
        {companyState !== 'hidden' && recipientsLoaded && (
          <>
            {label('Company access')}
            {alreadySharedWithCompany ? (
              <Alert severity="success">Shared with company. Every employee can open this report.</Alert>
            ) : canShareCompany ? (
              <Stack spacing={1}>
                <Typography variant="body2">Make this report available to every employee.</Typography>
                {(recipientsChanged || homeChanged) && (
                  <Alert severity="info">
                    Save the pending people or team changes before sharing with the company.
                  </Alert>
                )}
                {confirmCompanyShare ? (
                  <Alert severity="warning">
                    Everyone in the company will be able to open {currentLens.title}. Continue?
                  </Alert>
                ) : null}
                {companyShareError && <Alert severity="error">{companyShareError}</Alert>}
                <Stack direction="row" spacing={1}>
                  {confirmCompanyShare && (
                    <Button
                      disabled={busy}
                      onClick={() => {
                        setConfirmCompanyShare(false);
                        setCompanyShareError(null);
                      }}
                      variant="text"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    disabled={busy || recipientsChanged || homeChanged}
                    onClick={() => {
                      if (confirmCompanyShare) {
                        void confirmShareWithCompany();
                      } else {
                        setConfirmCompanyShare(true);
                      }
                    }}
                    variant="contained"
                  >
                    {shareCompany.isPending ? 'Sharing…' : confirmCompanyShare ? 'Confirm share' : 'Share with company'}
                  </Button>
                </Stack>
              </Stack>
            ) : null}
          </>
        )}
        {label('Team home')}
        {spacesLoading && <Typography variant="body2">Loading team spaces…</Typography>}
        {spacesError && <Alert severity="warning">We couldn’t load team spaces. Close this panel and try again.</Alert>}
        {writableSpaces.length === 0 && !currentSpaceId ? null : (
          <>
            <TextField
              disabled={!canManageSharing || busy}
              fullWidth
              label="Home"
              onChange={(event) => {
                setMoveTo(event.target.value);
                setMoveError(null);
                setSharingFeedback(null);
              }}
              select
              size="small"
              value={moveTo}
            >
              <MenuItem value="">Personal</MenuItem>
              {spaces
                .filter((space) => space.id === currentSpaceId || writableSpaces.includes(space))
                .map((space) => (
                  <MenuItem key={space.id} value={space.id} disabled={!writableSpaces.includes(space)}>
                    {space.name}
                  </MenuItem>
                ))}
            </TextField>
            {leavingSpace && (
              <TextField
                disabled={busy}
                fullWidth
                label="New personal manager"
                onChange={(event) => {
                  setNewManager(event.target.value);
                  setSharingFeedback(null);
                }}
                size="small"
                sx={{ mt: 1.5 }}
                value={newManager}
              />
            )}
          </>
        )}

        {(currentView || viewState) && (
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={includeView}
                onChange={(_, checked) => {
                  setIncludeView(checked);
                  setCopied(false);
                }}
              />
            }
            label="Include current filters, dates and comparison"
          />
        )}
        {open && (
          <LensShareExport
            key={currentLens.id}
            lens={currentLens}
            url={shareUrl}
            view={sharedView}
            disabled={busy || detail.isError}
          />
        )}
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          flexShrink: 0,
          bgcolor: tokens.surface,
          borderTop: '1px solid',
          borderColor: alpha(tokens.accent, 0.16),
          px: 3,
          py: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {moveError && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {moveError}
          </Alert>
        )}
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(shareUrl);
              setCopied(true);
              setCopyError(false);
            } catch {
              setCopied(false);
              setCopyError(true);
            }
          }}
          startIcon={<LinkOutlinedIcon />}
          sx={{ color: tokens.accent, fontWeight: 700 }}
        >
          Copy link
        </Button>
        <Stack direction="row" spacing={1}>
          <Button
            disabled={
              busy ||
              !recipientsLoaded ||
              (homeChanged && (!canManageSharing || (leavingSpace && !EMAIL_PATTERN.test(newManager.trim()))))
            }
            onClick={() => {
              if (canShare && (homeChanged || recipientsChanged)) {
                setMoveError(null);
                void saveChanges()
                  .then(onClose)
                  .catch((error: unknown) => {
                    setMoveError(error instanceof Error ? error.message : 'We couldn’t save this Lens. Try again.');
                  });
              } else {
                onClose();
              }
            }}
            variant="contained"
          >
            {updateSharing.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Stack>
      {copied && (
        <Typography aria-live="polite" color="text.secondary" sx={{ fontSize: 11.5, px: 3, pb: 2 }}>
          {linkReach}
        </Typography>
      )}
      {copyError && (
        <Alert severity="error" sx={{ borderRadius: 0, mx: 3, mb: 2 }}>
          We couldn’t copy the link. Copy it manually: {shareUrl}
        </Alert>
      )}
    </Dialog>
  );
}
